/**
 * APEXION - Exchange Hub Page
 * Unified interface for all exchanges (CEX, DEX, On-Ramp)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  ArrowLeft,
  Building2,
  Wallet,
  CreditCard,
  ExternalLink,
  Check,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';

const ExchangeHub = () => {
  const navigate = useNavigate();
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState('cex');
  const [walletStatus, setWalletStatus] = useState(null);

  useEffect(() => {
    loadExchanges();
    loadWalletStatus();
  }, []);

  const loadExchanges = async () => {
    try {
      const response = await api.get('/exchanges');
      setExchanges(response.data?.exchanges || []);
    } catch (error) {
      console.error('Failed to load exchanges:', error);
      toast.error('Failed to load exchanges');
    } finally {
      setLoading(false);
    }
  };

  const loadWalletStatus = async () => {
    try {
      const response = await api.get('/dex/wallet/status');
      setWalletStatus(response.data);
    } catch (error) {
      console.error('Failed to load wallet status:', error);
    }
  };

  const cexExchanges = exchanges.filter(e => e.type === 'cex');
  const dexExchanges = exchanges.filter(e => e.type === 'dex');
  const onrampExchanges = exchanges.filter(e => e.type === 'onramp');

  const getExchangeLogo = (id) => {
    const logos = {
      kraken: '🟣',
      coinbase: '🔵',
      uniswap: '🦄',
      pancakeswap: '🥞',
      jupiter: '🪐',
      moonpay: '🌙',
    };
    return logos[id] || '💱';
  };

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-[#94A3B8] hover:text-[#F5F7FA]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="text-xl font-bold font-['Outfit']">Exchange Hub</div>
          </div>
          {walletStatus?.connected && (
            <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
              <Wallet className="w-3 h-3 mr-1" />
              {walletStatus.address?.slice(0, 6)}...{walletStatus.address?.slice(-4)}
            </Badge>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Intro */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold font-['Outfit'] mb-2">Multi-Exchange Trading</h1>
            <p className="text-[#94A3B8]">
              Trade across centralized exchanges, decentralized protocols, and buy crypto with fiat.
            </p>
          </div>

          <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-6">
            <TabsList className="bg-[#15171C] border border-[#1E293B]">
              <TabsTrigger value="cex" className="data-[state=active]:bg-[#2F6BFF]">
                <Building2 className="w-4 h-4 mr-2" />
                CEX
              </TabsTrigger>
              <TabsTrigger value="dex" className="data-[state=active]:bg-[#2F6BFF]">
                <Wallet className="w-4 h-4 mr-2" />
                DEX
              </TabsTrigger>
              <TabsTrigger value="onramp" className="data-[state=active]:bg-[#2F6BFF]">
                <CreditCard className="w-4 h-4 mr-2" />
                Buy Crypto
              </TabsTrigger>
            </TabsList>

            {/* CEX Tab */}
            <TabsContent value="cex" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {cexExchanges.map((exchange) => (
                  <Card key={exchange.id} className="bg-[#15171C] border-[#1E293B] hover:border-[#2F6BFF] transition-colors">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{getExchangeLogo(exchange.id)}</span>
                          <div>
                            <CardTitle className="text-lg">{exchange.name}</CardTitle>
                            <CardDescription className="text-xs">
                              {exchange.supported_pairs?.length || 0} trading pairs
                            </CardDescription>
                          </div>
                        </div>
                        {exchange.testnet_available && (
                          <Badge variant="outline" className="text-xs">Testnet</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#94A3B8]">Maker Fee</span>
                          <span>{(exchange.fees?.maker * 100).toFixed(2)}%</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-[#94A3B8]">Taker Fee</span>
                          <span>{(exchange.fees?.taker * 100).toFixed(2)}%</span>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {exchange.supported_pairs?.slice(0, 4).map((pair) => (
                            <Badge key={pair} variant="outline" className="text-xs">
                              {pair}
                            </Badge>
                          ))}
                          {exchange.supported_pairs?.length > 4 && (
                            <Badge variant="outline" className="text-xs">
                              +{exchange.supported_pairs.length - 4}
                            </Badge>
                          )}
                        </div>
                        <Button
                          className="w-full mt-4 bg-[#2F6BFF] hover:bg-[#2F6BFF]/80"
                          onClick={() => navigate('/api-keys')}
                        >
                          Connect {exchange.name}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* DEX Tab */}
            <TabsContent value="dex" className="space-y-4">
              {/* Wallet Connection Status */}
              <Card className="bg-[#15171C] border-[#1E293B]">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Wallet className="w-5 h-5 text-[#2F6BFF]" />
                      <div>
                        <p className="font-medium">Wallet Connection</p>
                        <p className="text-sm text-[#94A3B8]">
                          {walletStatus?.connected 
                            ? `Connected: ${walletStatus.address?.slice(0, 8)}...${walletStatus.address?.slice(-6)}`
                            : 'Connect your wallet to trade on DEXs'
                          }
                        </p>
                      </div>
                    </div>
                    <Button
                      variant={walletStatus?.connected ? 'outline' : 'default'}
                      className={walletStatus?.connected ? '' : 'bg-[#2F6BFF]'}
                      onClick={() => navigate('/dex/connect')}
                    >
                      {walletStatus?.connected ? 'Change Wallet' : 'Connect Wallet'}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {dexExchanges.map((exchange) => (
                  <Card key={exchange.id} className="bg-[#15171C] border-[#1E293B] hover:border-[#2F6BFF] transition-colors">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getExchangeLogo(exchange.id)}</span>
                        <div>
                          <CardTitle className="text-lg">{exchange.name}</CardTitle>
                          <CardDescription className="text-xs">
                            Decentralized Exchange
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="text-sm text-[#94A3B8]">Supported Chains:</div>
                        <div className="flex flex-wrap gap-1">
                          {exchange.chains?.map((chain) => (
                            <Badge key={chain} variant="outline" className="text-xs capitalize">
                              {chain}
                            </Badge>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-[#94A3B8] mt-2">
                          <AlertCircle className="w-4 h-4" />
                          Requires wallet connection
                        </div>
                        <Button
                          className="w-full mt-4 bg-[#2F6BFF] hover:bg-[#2F6BFF]/80"
                          onClick={() => navigate(`/dex/${exchange.id}`)}
                          disabled={!walletStatus?.connected && exchange.id !== 'jupiter'}
                        >
                          Trade on {exchange.name}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>

            {/* On-Ramp Tab */}
            <TabsContent value="onramp" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {onrampExchanges.map((exchange) => (
                  <Card key={exchange.id} className="bg-[#15171C] border-[#1E293B] hover:border-[#C9A24D] transition-colors">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{getExchangeLogo(exchange.id)}</span>
                        <div>
                          <CardTitle className="text-xl">{exchange.name}</CardTitle>
                          <CardDescription>
                            Buy crypto with credit card, bank transfer, and more
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <p className="text-sm text-[#94A3B8] mb-2">Supported Fiat:</p>
                          <div className="flex flex-wrap gap-1">
                            {exchange.supported_fiat?.map((currency) => (
                              <Badge key={currency} className="bg-green-500/20 text-green-400">
                                {currency}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-[#94A3B8] mb-2">Supported Crypto:</p>
                          <div className="flex flex-wrap gap-1">
                            {exchange.supported_crypto?.map((crypto) => (
                              <Badge key={crypto} variant="outline" className="text-xs">
                                {crypto}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Button
                          className="w-full mt-4 bg-[#C9A24D] hover:bg-[#C9A24D]/80 text-[#0B0D12]"
                          onClick={() => navigate('/buy-crypto')}
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Buy Crypto Now
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </main>
    </div>
  );
};

export default ExchangeHub;
